import React, { useState, useRef } from 'react'
import type { Block } from '../../editor/types'

interface StepsBlockProps {
  block: Block
  variableValues?: Record<string, number | string | boolean>
  onSetVariableValue?: (name: string, value: number | string | boolean) => void
}

type StepsStyle = 'dots' | 'numbered' | 'timeline' | 'progress-bar'

export default function StepsBlock({ block, variableValues, onSetVariableValue }: StepsBlockProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const steps = (config.steps as string[]) || []
  const style = (config.variant as StepsStyle) || 'dots'
  const [localStep, setLocalStep] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const hasVariable = !!(block.variable && variableValues && onSetVariableValue)
  const currentStep = hasVariable
    ? Number(variableValues![block.variable!] ?? 0)
    : localStep
  const goTo = (i: number) => {
    const clamped = Math.max(0, Math.min(steps.length - 1, i))
    if (hasVariable) {
      onSetVariableValue!(block.variable!, clamped)
    } else {
      setLocalStep(clamped)
    }
    // Scroll back to stepper when changing step
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (steps.length === 0) {
    return <p className="text-text-secondary text-sm">No steps defined</p>
  }

  const accent = block.accent || 'var(--accent, #5048e5)'

  return (
    <div ref={containerRef} className="space-y-3">
      {/* Progress indicator */}
      {style === 'dots' && (
        <div className="flex gap-1.5">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="h-2 flex-1 rounded-full transition-colors duration-200 cursor-pointer"
              style={{
                backgroundColor: i <= currentStep ? accent : 'var(--color-canvas, #e2e8f0)',
              }}
            />
          ))}
        </div>
      )}

      {style === 'numbered' && (
        <div className="flex items-center gap-1">
          {steps.map((_, i) => (
            <React.Fragment key={i}>
              <button
                onClick={() => goTo(i)}
                className="w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center transition-all cursor-pointer shrink-0"
                style={{
                  backgroundColor: i <= currentStep ? accent : 'transparent',
                  color: i <= currentStep ? 'white' : '#94a3b8',
                  border: i <= currentStep ? 'none' : '2px solid #e2e8f0',
                }}
              >
                {i + 1}
              </button>
              {i < steps.length - 1 && (
                <div
                  className="flex-1 h-0.5 rounded-full transition-colors duration-200"
                  style={{ backgroundColor: i < currentStep ? accent : '#e2e8f0' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {style === 'progress-bar' && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--block-text, #94a3b8)', opacity: 0.6 }}>
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-[10px] font-bold" style={{ color: accent }}>
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden cursor-pointer" onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect()
            const pct = (e.clientX - rect.left) / rect.width
            goTo(Math.round(pct * (steps.length - 1)))
          }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%`, backgroundColor: accent }}
            />
          </div>
        </div>
      )}

      {style === 'timeline' && (
        <div className="space-y-0">
          {steps.map((step, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="flex items-start gap-3 w-full text-left cursor-pointer group"
            >
              <div className="flex flex-col items-center">
                <div
                  className="w-3 h-3 rounded-full shrink-0 transition-all border-2"
                  style={{
                    backgroundColor: i <= currentStep ? accent : 'white',
                    borderColor: i <= currentStep ? accent : '#e2e8f0',
                  }}
                />
                {i < steps.length - 1 && (
                  <div
                    className="w-0.5 h-8 transition-colors"
                    style={{ backgroundColor: i < currentStep ? accent : '#e2e8f0' }}
                  />
                )}
              </div>
              <span
                className={`text-sm pt-px transition-colors ${
                  i === currentStep ? 'font-semibold' : i < currentStep ? 'text-slate-500' : 'text-slate-400'
                }`}
                style={i === currentStep ? { color: accent } : undefined}
              >
                {step}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Step title (not for timeline which shows all steps, not for progress-bar which shows its own) */}
      {style !== 'timeline' && (
        <p className="font-medium" style={{ color: 'var(--block-text, #0f172a)' }}>
          {steps[currentStep]}
        </p>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => goTo(currentStep - 1)}
          disabled={currentStep === 0}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          style={{
            borderColor: accent,
            color: accent,
          }}
        >
          Previous
        </button>
        <button
          onClick={() => goTo(currentStep + 1)}
          disabled={currentStep === steps.length - 1}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-all text-white disabled:opacity-30 disabled:cursor-not-allowed"
          style={{
            backgroundColor: accent,
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}
